// Client-side image compression to fix slow uploads & "image too large" errors.
// Camera photos from modern phones are 5–10 MB. This resizes to a max edge
// of 1600px and re-encodes as JPEG (quality 0.82), typically bringing a 6 MB
// photo down to 200–400 KB while keeping the photo visually identical for a
// product-catalogue use-case.

const MAX_EDGE = 1600;
const QUALITY = 0.82;

/**
 * Compress an image File. Returns a new File (or the original if already small
 * or unsupported). Never throws — falls back to the original file on any error
 * so uploads keep working even if canvas encoding fails on odd formats.
 */
export async function compressImage(file, { maxEdge = MAX_EDGE, quality = QUALITY } = {}) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // Skip tiny images — no benefit and re-encoding can slightly enlarge them.
  if (file.size <= 400 * 1024) return file;
  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const { width, height } = img;
    let w = width;
    let h = height;
    if (Math.max(width, height) > maxEdge) {
      if (width >= height) {
        w = maxEdge;
        h = Math.round((height / width) * maxEdge);
      } else {
        h = maxEdge;
        w = Math.round((width / height) * maxEdge);
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;
    // If compression somehow made it larger, keep the original.
    if (blob.size >= file.size) return file;
    const base = (file.name || "photo").replace(/\.[^.]+$/, "");
    return new File([blob], `${base}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch {
    return file;
  }
}
